import { Icon } from "@atoms";
import Spinner from "@legacy/core/components/Spinner";
import { faIcon } from "@utils/faIcon";
import { Component, Fragment } from "react";
import ButtonGroup from "../../core/buttons/ButtonGroup";
import ButtonGroupRow from "../../core/buttons/ButtonGroupRow";
import UniversalButton from "../../core/buttons/UniversalButton";
import ContactListDisplay from "../../core/components/ContactListDisplay";
import BasicDisplayField from "../../core/fields/BasicDisplayField";
import { valueIsDefined } from "../../core/utils/utils";
import { getPriceBookTaxObjectLabel } from "../../pricebook/inputs/PriceBookTaxSearchOrCreateSelect";
import { getPricebookTaxById } from "../../pricebook/utils/utils";
import { getClientDefaultBillingIntentionDisplay, renderClientString, renderServiceLocationString } from "../utils/utils";


class ServiceLocationMergeConfirmDisplay extends Component {

    constructor(props) {
        super(props)
        this.priceBookTaxes = window.PRICEBOOK_TAXES || []
    }

    componentDidMount() {
        // Return to the previous scroll
        document.querySelector(".main").scrollTo(0, this.props.returnScroll || 0)
    }

    renderButtons = () => {
        const { mode, submitting, errors, client, sourceServiceLocation, destinationServiceLocation, requestAction, switchToPrimaryForm, returnScroll} = this.props

        if (errors.unexpectedError) {
            return (
                <div className="data-panel__action-feedback">
                    <span className="text-invalid"><strong>An unexpected error occurred.</strong></span>
                </div>
            )
        }
        else {
            if (submitting) {
                return (
                    <div className="data-panel__action-feedback">
                        <Spinner centered={true} />
                    </div>
                )
            }
            else {
                return (
                    <ButtonGroup>
                        <ButtonGroupRow>
                            <UniversalButton type="primary" text="Confirm Merge" handler={event => requestAction("MERGE")} />
                        </ButtonGroupRow>
                        <ButtonGroupRow>
                            <UniversalButton type="secondary" text="Change Locations" handler={event => switchToPrimaryForm()} />
                        </ButtonGroupRow>
                    </ButtonGroup>
                )
            }
        }
    }

    renderMergeDetails = (destinationServiceLocation) => {
        let detailUrl = DjangoUrls["clients:clients-detail"](window.MARKETPLACE_ENTITY_SLUG, this.props.client.id)

        return <Fragment>{"Merging with "}<a className="text-link text-link--inline" href={detailUrl} target="_blank">{renderServiceLocationString(destinationServiceLocation)}&nbsp;&nbsp;<Icon containerClassName="icon-inline" icon={faIcon({ name: "arrow-up-right-from-square", style: "regular" })} /></a></Fragment>
    }

    renderAddedText = () => {
        return <span className="data-panel__form__field__success"><Icon containerClassName="icon-inline" icon={faIcon({ name: "square-plus", style: "regular" })} />&nbsp;&nbsp;This will be added to the destination service location.</span>
    }

    renderIgnoredText = () => {
        return <span className="data-panel__form__field__warning"><Icon containerClassName="icon-inline" icon={faIcon({ name: "triangle-exclamation", style: "regular" })} />&nbsp;&nbsp;This does not match the destination service location and will be ignored.</span>
    }

    renderMatchFieldText = (source, destination, fieldName) => {
        let sourceValue = source[fieldName]
        let destinationValue = destination[fieldName]

        if (valueIsDefined(sourceValue) && !valueIsDefined(destinationValue)) {
            return this.renderAddedText()
        }
        else if (valueIsDefined(sourceValue) && sourceValue !== destinationValue) {
            return this.renderIgnoredText()
        }
    }

    renderBillingIntentionMatchText = (source, destination) => {
        // Mirrors the merge: the intention/client pair is copied only onto a destination that hasn't set
        // one, and never when the source bills to the destination's own client
        const sourceBillingClientId = source.default_billing_client !== null ? source.default_billing_client.id : null
        const destinationBillingClientId = destination.default_billing_client !== null ? destination.default_billing_client.id : null

        if (valueIsDefined(source.default_billing_intention) && !valueIsDefined(destination.default_billing_intention) && sourceBillingClientId !== destination.external_client) {
            return this.renderAddedText()
        }
        else if (valueIsDefined(source.default_billing_intention) && (source.default_billing_intention !== destination.default_billing_intention || sourceBillingClientId !== destinationBillingClientId)) {
            return this.renderIgnoredText()
        }
    }

    renderTaxRate = (serviceLocation) => {
        if (valueIsDefined(serviceLocation.default_pricebook_tax)) {
            const tax = getPricebookTaxById(serviceLocation.default_pricebook_tax, this.priceBookTaxes)
            return tax ? getPriceBookTaxObjectLabel(tax) : "--"
        }
        else {
            return "Same as client default"
        }
    }

    render() {
        const { mode, submitting, errors, client, sourceServiceLocation, destinationServiceLocation, requestAction, switchToPrimaryForm, returnScroll} = this.props

        return (
            <div className="data-panel-container data-panel-container--with-margin" style={{width: "100%"}}>
                <div className="data-panel" aria-label="Service Location">
                    <div className="data-panel__heading" aria-label="Client/Service Location Information">
                        <span className="data-panel__heading__title" aria-label="Name">{renderClientString(client)}</span>
                        <span className="data-panel__heading__subtitle" aria-label="Display">{renderServiceLocationString(sourceServiceLocation)}</span>
                        <hr aria-hidden="true" />
                    </div>
                    <div className="data-panel__form" aria-label="Service Location Merge Display">
                        <BasicDisplayField
                            fieldName="merge_details"
                            fieldLabel="Merge Details"
                            fieldValue={this.renderMergeDetails(destinationServiceLocation)}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="name"
                            fieldLabel="Location Name"
                            fieldValue={sourceServiceLocation.name || "--"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "name")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="physical_address"
                            fieldLabel="Location Address"
                            fieldValue={sourceServiceLocation.physical_address_formatted_multiline || "--"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "physical_address_formatted")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="billing_address"
                            fieldLabel="Location Billing Address"
                            fieldValue={sourceServiceLocation.billing_address_formatted_multiline || "--"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "billing_address_formatted")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="notes"
                            fieldLabel="Gate Codes, Hazards, etc."
                            fieldValue={sourceServiceLocation.notes || "--"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "notes")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="default_estimate_expiration"
                            fieldLabel="Default Estimate Expiration"
                            fieldValue={sourceServiceLocation.default_estimate_net !== null ? (sourceServiceLocation.default_estimate_net === 0 ? "Same Day" : `${sourceServiceLocation.default_estimate_net} days`) : "Same as client default"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "default_estimate_net")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="default_payment_terms"
                            fieldLabel="Default Location Invoice Payment Terms"
                            fieldValue={sourceServiceLocation.default_invoice_net !== null ? (sourceServiceLocation.default_invoice_net === 0 ? "Same Day" : `Net ${sourceServiceLocation.default_invoice_net}`) : "Same as client default"}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "default_invoice_net")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="default_pricebook_tax"
                            fieldLabel="Default Location Tax Rate"
                            fieldValue={this.renderTaxRate(sourceServiceLocation)}
                            footer={this.renderMatchFieldText(sourceServiceLocation, destinationServiceLocation, "default_pricebook_tax")}
                        ></BasicDisplayField>
                        <BasicDisplayField
                            fieldName="default_billing_intention"
                            fieldLabel="Default Billing Intention"
                            fieldValue={valueIsDefined(sourceServiceLocation.default_billing_intention) ? getClientDefaultBillingIntentionDisplay(sourceServiceLocation.default_billing_intention, sourceServiceLocation.default_billing_client) : "Same as client default"}
                            footer={this.renderBillingIntentionMatchText(sourceServiceLocation, destinationServiceLocation)}
                        ></BasicDisplayField>
                        <ContactListDisplay
                            fieldName="contacts"
                            fieldLabel="Location Contacts"
                            contacts={sourceServiceLocation.contacts}
                            primaryContactId={sourceServiceLocation.primary_contact}
                            header={<span className="data-panel__form__field__success" style={{margin: "0 0 1em"}}><Icon containerClassName="icon-inline" icon={faIcon({ name: "square-plus", style: "regular" })} />&nbsp;&nbsp;These will be merged into the destination client.</span>}
                        ></ContactListDisplay>
                    </div>
                    {this.renderButtons()}
                </div>
            </div>
        )
    }
}

export default ServiceLocationMergeConfirmDisplay;
